import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import Sidebar from '../Sidebar/Sidebar';
import { checkSession } from '../../../store/slices/authSlice';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import './Layout.css';

const LAST_VISITED_KEY = 'lastVisitedPath';

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, sessionExpired } = useSelector((state) => state.auth);
  const { updateBreadcrumbs } = useBreadcrumb();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true; // Default open
    return window.innerWidth > 1024; // Default open on desktop, closed on mobile
  });
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 1024;
  });


  // Periodic session check (every 5 minutes)
  useEffect(() => {
    if (!user) return;

    const sessionCheckInterval = setInterval(() => {
      dispatch(checkSession({ isBackground: true }));
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      clearInterval(sessionCheckInterval);
    };
  }, [user, dispatch]);

  // Handle session timeout removed - centralized in App.jsx

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobileView(mobile);
      // We don't auto-open sidebar anymore, it stays collapsed/closed by default
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.pathname === '/login') {
      sessionStorage.removeItem(LAST_VISITED_KEY);
      return;
    }
    const composedPath = `${location.pathname}${location.search}${location.hash}`;
    sessionStorage.setItem(LAST_VISITED_KEY, composedPath);
  }, [location.pathname, location.search, location.hash]);

  const layoutClasses = [
    'mainsection',
    sidebarOpen ? 'sidebar-open' : 'sidebar-collapsed',
    isMobileView && sidebarOpen ? 'mobile-sidebar-open' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={layoutClasses}>
      {/* Header */}
      <Header
        user={user}
        onToggleSidebar={toggleSidebar}
        sidebarOpen={sidebarOpen}
      />

      {/* Sidebar */}
      <Sidebar
        user={user}
        isOpen={sidebarOpen}
        onClose={closeSidebar}
        isMobileView={isMobileView}
      />

      {isMobileView && sidebarOpen && (
        <div
          className="sidebar-overlay"
          role="presentation"
          onClick={closeSidebar}
        />
      )}

      {/* Main Content */}
      <div className="right-panel">
        <div className="main-content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
